import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Papa from 'papaparse';
import userCsv from './assets/users.csv';

// Import Components & Pages
import MainLayout from './components/MainLayout';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import LoginPage from './pages/LoginPage';
import HomePage from './pages/HomePage';
import AgentListPage from './pages/AgentListPage';
import AgentUseCasePage from './pages/AgentUseCasePage';
import PlaceholderPage from './pages/PlaceholderPage';

function App() {
  // 1. Store the full user object, or null if not logged in
  const [currentUser, setCurrentUser] = useState(null); 
  const [users, setUsers] = useState([]);
  // 1. Add theme state
  const [theme, setTheme] = useState('light');

  // 2. Function to toggle the theme
  const toggleTheme = () => {
    setTheme(prevTheme => (prevTheme === 'light' ? 'dark' : 'light'));
  };

  // 3. Effect to apply the theme class to the body
  useEffect(() => {
    document.body.className = ''; // Clear previous theme
    document.body.classList.add(theme + '-theme');
  }, [theme]);


  useEffect(() => {
    const parseLocalCsv = async () => {
      Papa.parse(userCsv, {
        download: true,
        header: true,
        complete: (results) => {
          const cleanedData = results.data.filter(user => user.username);
          setUsers(cleanedData);
        },
      });
    };
    parseLocalCsv();
  }, []);

  // 2. The login handler now calls the server
  // Helper: convert PEM to ArrayBuffer for Web Crypto
  const pemToArrayBuffer = (pem) => {
    const b64 = pem.replace(/-----BEGIN PUBLIC KEY-----|-----END PUBLIC KEY-----|\n|\r/g, '');
    const binary = atob(b64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
    return bytes.buffer;
  };

  // Import PEM public key (SPKI) into Web Crypto
  const importPublicKey = async (pem) => {
    const ab = pemToArrayBuffer(pem);
    return await window.crypto.subtle.importKey(
      'spki',
      ab,
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256',
      },
      false,
      ['encrypt']
    );
  };

  const encryptPayloadWithServerKey = async (payloadObj) => {
    // Fetch server public key
    const r = await fetch('http://localhost:5000/api/pubkey');
    const { publicKey } = await r.json();
    const key = await importPublicKey(publicKey);
    const encoder = new TextEncoder();
    const data = encoder.encode(JSON.stringify(payloadObj));
    const encrypted = await window.crypto.subtle.encrypt({ name: 'RSA-OAEP' }, key, data);
    // Convert to base64
    const u8 = new Uint8Array(encrypted);
    let binary = '';
    for (let i = 0; i < u8.byteLength; i++) binary += String.fromCharCode(u8[i]);
    return btoa(binary);
  };

  const handleLogin = async (username, password) => {
    try {
      // Encrypt credentials before sending
      const payload = await encryptPayloadWithServerKey({ username, password });

      const response = await fetch('http://localhost:5000/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ payload }),
      });

      const result = await response.json();

      if (result.success) {
        setCurrentUser(result.user); // Set the user object in state
        return true;
      }

      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  // Handle signup for new users
  const handleSignup = async (username, password) => {
    try {
      // Encrypt credentials before sending to server
      const payload = await encryptPayloadWithServerKey({ username, password });
      const response = await fetch('http://localhost:5000/api/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ payload }),
      });

      const result = await response.json();

      if (result.success) {
        // Add the new user to users state WITHOUT storing the plaintext password
        const newUser = { username };
        setUsers(prevUsers => [...prevUsers, newUser]);
        return { success: true, message: result.message };
      }

      return result;
    } catch (error) {
      console.error('Signup error:', error);
      return { success: false, message: 'Failed to connect to server.' };
    }
  };
  const handleLogout = () => {
    localStorage.removeItem('currentUser'); // Clear session
    setCurrentUser(null);
  };

  return (
    <Router basename='/runbook'>
      <ScrollToTop />
      <Routes>
        <Route path="/login" element={<LoginPage onLogin={handleLogin} onSignup={handleSignup} />} />

        {/* 3. The protected route now checks for 'currentUser' */}
        <Route element={<ProtectedRoute isLoggedIn={!!currentUser} />}>
          {/* 4. Pass the 'currentUser' down to the MainLayout */}
          <Route path="/" element={<MainLayout user={currentUser} theme={theme} toggleTheme={toggleTheme} onLogout={handleLogout}/>}>
            <Route index element={<HomePage />} />
            <Route path="/:domain/:submenu" element={<AgentListPage />} />
            <Route path="/:domain/:submenu/:agentId" element={<AgentUseCasePage />} />
            <Route path="/:domain" element={<PlaceholderPage />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Route>
        </Route>
      </Routes>
    </Router>
  );
}

export default App;